import React, { useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../style/theme';
import CloseButton from './shared/CloseButton';

// Backdrop overlay
const Backdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  z-index: ${theme.zIndex.modal};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity ${theme.transitions.fast}, visibility ${theme.transitions.fast};
  
  /* Prevent any flicker or flash */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: opacity;
  
  &.modal-enter {
    opacity: 0;
    visibility: visible;
  }
  
  &.modal-enter-active {
    opacity: 1;
    transition: opacity ${theme.transitions.fast};
  }
  
  &.modal-exit {
    opacity: 1;
  }
  
  &.modal-exit-active {
    opacity: 0;
    transition: opacity ${theme.transitions.fast};
  }
`;

// Modal container
const ModalContainer = styled.div<{ 
  isOpen: boolean; 
  maxWidth?: string;
  $top?: number;
  $left?: number;
}>`
  background: ${theme.colors.neutral.white};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows['2xl']};
  max-width: ${props => props.maxWidth || '500px'};
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  position: absolute;
  
  /* Prevent flicker and improve performance */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: transform;
  
  /* Dynamic positioning based on viewport and scroll */
  top: ${props => props.$top !== undefined ? `${props.$top}px` : '50%'};
  left: ${props => props.$left !== undefined ? `${props.$left}px` : '50%'};
  
  /* Transform with proper centering and smooth animations */
  transform: ${props => {
    const scale = props.isOpen ? 'scale(1)' : 'scale(0.95)';
    const translateY = props.isOpen ? 'translateY(0)' : 'translateY(20px)';
    return `translate(-50%, -50%) ${scale} ${translateY}`;
  }};
  
  /* Smooth transition only for scale and translateY, not position */
  transition: transform ${theme.transitions.fast};
  
  /* Ensure immediate positioning without transition */
  transition-property: transform;
  
  /* Ensure proper centering on all screen sizes */
  @media (max-height: 600px) {
    max-height: 95vh;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.colors.primary.gradient};
    border-radius: ${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0;
  }
`;

// Modal header
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[6]} ${theme.spacing[6]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.neutral.gray200};
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.neutral.gray800};
  margin: 0;
  line-height: ${theme.typography.lineHeight.tight};
`;

// Modal content
const ModalContent = styled.div<{ hasStandaloneCloseButton?: boolean }>`
  padding: ${props => props.hasStandaloneCloseButton 
    ? `${theme.spacing[8]} ${theme.spacing[6]} ${theme.spacing[6]}` 
    : theme.spacing[6]
  };
  overflow-y: auto;
  max-height: calc(90vh - 140px); // Account for header and potential footer
`;

// Close button wrapper
const CloseButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.lg};
  transition: all ${theme.transitions.fast};
  cursor: pointer;
  background-color: ${theme.colors.neutral.gray50};
  border: 1px solid ${theme.colors.neutral.gray200};
  
  &:hover {
    background-color: ${theme.colors.status.error}10;
    border-color: ${theme.colors.status.error}30;
    transform: scale(1.05);
  }
  
  &:active {
    background-color: ${theme.colors.status.error}20;
    transform: scale(0.95);
  }
  
  /* Focus styles for accessibility */
  &:focus {
    outline: 2px solid ${theme.colors.primary.main};
    outline-offset: 2px;
  }
`;

// Standalone close button for modals without titles
const StandaloneCloseButton = styled.div`
  position: absolute;
  top: ${theme.spacing[4]};
  right: ${theme.spacing[4]};
  z-index: 10;
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  maxWidth 
}) => {
  // Initialize position immediately to prevent flash
  const [scrollPosition, setScrollPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      return {
        x: scrollX + (viewportWidth / 2),
        y: scrollY + (viewportHeight / 2)
      };
    }
    return { x: 0, y: 0 };
  });

  // Handle escape key
  const handleEscape = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Update modal position when opened and handle cleanup
  useEffect(() => {
    if (isOpen) {
      // Recalculate position when modal opens (in case viewport changed)
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const centerX = scrollX + (viewportWidth / 2);
      const centerY = scrollY + (viewportHeight / 2);
      
      // Only update if position actually changed to avoid unnecessary re-renders
      setScrollPosition(prev => {
        if (prev.x !== centerX || prev.y !== centerY) {
          return { x: centerX, y: centerY };
        }
        return prev;
      });
      
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) {
    return null;
  }

  return (
    <Backdrop 
      isOpen={isOpen} 
      onClick={handleBackdropClick}
    >
      <ModalContainer 
        isOpen={isOpen} 
        maxWidth={maxWidth}
        $top={scrollPosition.y}
        $left={scrollPosition.x}
      >
        {title && (
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
            <CloseButtonWrapper 
              onClick={onClose}
              tabIndex={0}
              role="button"
              aria-label="Close modal"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClose();
                }
              }}
            >
              <CloseButton onClick={onClose} />
            </CloseButtonWrapper>
          </ModalHeader>
        )}
        
        {/* Always show close button, even without title */}
        {!title && (
          <StandaloneCloseButton>
            <CloseButtonWrapper 
              onClick={onClose}
              tabIndex={0}
              role="button"
              aria-label="Close modal"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClose();
                }
              }}
            >
              <CloseButton onClick={onClose} />
            </CloseButtonWrapper>
          </StandaloneCloseButton>
        )}
        
        <ModalContent hasStandaloneCloseButton={!title}>
          {children}
        </ModalContent>
      </ModalContainer>
    </Backdrop>
  );
};

export default Modal;
